/** Resolves a comic page's displayable image URI, preferring an inline
 *  base64/url and otherwise loading by imageId through the bounded loader. */

import { useEffect, useState } from "react";
import { subscribeImage, peekImage } from "../lib/imageLoader";

interface PageImageSource {
  imageId?: string;
  imageBase64?: string;
  imageUrl?: string;
}

function inlineUri(page: PageImageSource | undefined): string | null {
  return page?.imageUrl || (page?.imageBase64 ? `data:image/jpeg;base64,${page.imageBase64}` : null);
}

export function usePageImage(page: PageImageSource | undefined): string | null {
  const inline = inlineUri(page);
  const imageId = page?.imageId;
  const [uri, setUri] = useState<string | null>(() => {
    if (inline) return inline;
    const cached = peekImage(imageId);
    return cached ? `data:image/jpeg;base64,${cached}` : null;
  });

  useEffect(() => {
    if (inline) {
      setUri(inline);
      return;
    }
    if (!imageId) {
      setUri(null);
      return;
    }
    const cached = peekImage(imageId);
    setUri(cached ? `data:image/jpeg;base64,${cached}` : null);
    return subscribeImage(imageId, (base64) => {
      setUri(`data:image/jpeg;base64,${base64}`);
    });
  }, [inline, imageId]);

  return uri;
}
